/**
 * **The Ankiety tab's left-hand list.** KANON `ankiety-klienta-2026-09-16`, `szkice/kierunek-a`.
 *
 * Every occurrence of every form, newest first, grouped by month. Pressing one replaces the pane on
 * the right; nothing navigates and nothing opens.
 *
 * ── It scrolls INSIDE its card, and that is not the double scroll the owner banned ───────────
 * The matrix's inner scroller was a second bar beside the tab's own over the SAME content — the
 * trainer scrolled the outer one and the table did not move. This is a list of two hundred rows
 * standing beside a pane that is read at its own length: sticking the card under the strip and
 * scrolling its rows is what makes both readable at once, and the tab's own scroll still belongs to
 * the pane. The two bars are never over the same thing.
 *
 * ── The COMPARE control is on this card's head, beside the rows it acts on ───────────────────
 * Owner, 16 September 2026: it stood at the far right of the pane, which is the other end of the
 * screen from where the trainer picks the two occurrences. Entering the mode pre-checks the
 * occurrence the pane is already showing, so the form is anchored before the first press; the head
 * then says what the checkboxes are for and carries the way out of the mode.
 *
 * It is ONE button in that slot, in both modes. Two components swapping places would be unmounted
 * and rebuilt on the press that swaps them, and the focus ring would fall to `<body>` every time a
 * trainer entered or left the mode; the same element with a different name on it keeps it.
 *
 * ── The anchor comes from the MODEL, never from the rows on screen ───────────────────────────
 * Which form a comparison is anchored to is `stream.index`, which covers every occurrence in the
 * read. Looked up in the drawn rows it went missing the moment a form chip filtered the anchor's
 * own row out — and a `null` anchor makes every row of every form checkable, which is the one thing
 * `compareGroup` exists to prevent. The chips are refused while the mode is on for the same reason.
 *
 * ── Compare mode is the SAME list with a checkbox in front of each row ───────────────────────
 * Not a second list and not a dialog. A comparison may only pair two occurrences of one form — the
 * questions are different otherwise — so once the first is checked, every row of another form is
 * REFUSED rather than hidden: it keeps `aria-disabled`, stays focusable, and its name says why. A
 * row that vanished when the trainer picked a form would teach nothing about the rule.
 *
 * ── Only an UNREVIEWED occurrence carries a dot ──────────────────────────────────────────────
 * Owner, 16 September 2026. A grey dot on every row already read was a mark on the majority saying
 * nothing to act on, and it made the ember one harder to find rather than easier. So „przejrzana"
 * is the absence of a mark, the dot is solid ember, and the two-line legend under the list went
 * with the grey one — there is only one mark left to explain, and the row's own accessible name
 * says what it is.
 *
 * ── A missed occurrence is drawn and is not a control ────────────────────────────────────────
 * It has no answers, so there is no report to open. The fact belongs on the list, the affordance
 * does not — so it is quieter INK carrying its own chip at full contrast. Never an `opacity`: a
 * refused row still has to be read, and a dim takes the chip and the date down with the title.
 */

import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useOverflowEdge } from '~/shared/lib/useOverflowEdge';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type {
  SurveyStreamChipTone,
  SurveyStreamRow,
  SurveyStreamView,
} from '../model/surveyStream';
import { CloseIcon, CompareIcon, RefusableIconButton } from '~/features/survey-report';
import styles from './SurveysTab.module.css';

/** The chip's modifier per tone. „czeka" wears the bare `.statusChip`: a state, not news. */
const CHIP_TONE_CLASS: Record<SurveyStreamChipTone, string> = {
  warn: String(styles.statusWarn),
  ghost: String(styles.statusMissed),
  neutral: '',
};

/** How many skeleton rows stand in the reserve while the read is open. */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5, 6] as const;

export interface SurveyStreamProps {
  /**
   * The card, so the tab can put focus back on one of its rows.
   *
   * The tab owns it rather than this component handing one out: `react-hooks/refs` refuses a ref
   * reached through an object a hook returned, and the element's identity reads better where it is
   * attached anyway. See `SurveysTab`'s own `focusRow`.
   */
  readonly ref?: RefObject<HTMLElement | null>;
  readonly stream: SurveyStreamView;
  /** The read hit the 200-occurrence cap: older history exists and is not listed. */
  readonly truncated: boolean;
  /** The occurrence the pane is showing. */
  readonly currentId: number | null;
  /**
   * The occurrences checked for a comparison, or `null` when the trainer is not comparing.
   *
   * An empty array is a real state — compare mode with nothing picked yet — and is not the same as
   * `null`. The first entry decides which form the other rows may come from.
   */
  readonly comparing: readonly number[] | null;
  readonly onSelect: (id: number) => void;
  readonly onToggleCompare: (id: number) => void;
  /** „Porównujesz: wybierz 2" — the tab's own sentence about the mode. `null` outside it. */
  readonly modeNote: string | null;
  /** Enter compare mode. The caller pre-checks the occurrence the pane is showing. */
  readonly onEnterCompare: () => void;
  /** Leave it, checks and all. */
  readonly onExitCompare: () => void;
  readonly onChip: (id: string | null) => void;
  /**
   * The caller's wait: `gate.waiting` picks the branch and the card's `aria-busy`, `gate.skeleton`
   * the bars inside it. One value, because a card still holding bars is still about to change.
   */
  readonly gate: SkeletonGate;
  readonly failed: boolean;
}

export function SurveyStream({
  ref,
  stream,
  truncated,
  currentId,
  comparing,
  onSelect,
  onToggleCompare,
  modeNote,
  onEnterCompare,
  onExitCompare,
  onChip,
  gate,
  failed,
}: SurveyStreamProps) {
  const { t } = useTranslation();
  /* A scrolling box is a tab stop only while it has something to scroll — the same rule the chart
   * strip follows. MEASURED rather than counted: the card's ceiling is a `dvh` calculation, so
   * whether eleven rows overflow is the window's answer and not the list's. Both edges, because a
   * stream scrolled to its end still scrolls — dropping the tab stop there would take focus off the
   * region the trainer is standing in. */
  const { ref: scrollerRef, hasMore, hasPrevious } = useOverflowEdge<HTMLDivElement>('block');
  const scrolls = hasMore || hasPrevious;

  const rows = stream.months.flatMap((month) => month.rows);
  /** Which form the comparison has been anchored to. `null` until the first row is checked. */
  const anchorId = comparing?.[0];
  const anchor =
    anchorId === undefined ? null : (stream.index.get(anchorId)?.compareGroup ?? null);
  /** Nothing to compare until two occurrences have actually been answered. */
  const comparable = rows.filter((row) => row.selectable).length > 1;
  /* The client has never been sent a survey. The card shrinks to its head and the pane beside it
   * carries the whole statement — a reserved list with a sentence in it, an `EmptyState` and a
   * silent chart strip is one fact said three times. A FAILED read keeps its reserve: the box it
   * held while the read was open must not collapse under the answer. */
  const bare = !gate.waiting && !failed && stream.empty;

  return (
    <section ref={ref} className={styles.streamCard} aria-busy={gate.waiting}>
      <div className={styles.streamHead}>
        <h2 className={styles.streamHeading}>{t('web:clientDetail.surveys.streamHeading')}</h2>
        {/* The ember count is the one mark on this card: how many answers are still waiting to be
          * read. A total would be a number nobody acts on. */}
        {stream.waitingCount === 0 ? null : (
          <b className={styles.streamCount}>
            {String(stream.waitingCount)}
            <span className={hidden.srOnly}> {t('web:clientDetail.surveys.legendWaiting')}</span>
          </b>
        )}
        {modeNote === null ? null : <em className={styles.streamMode}>{modeNote}</em>}
        {/* ONE control in this slot, in both modes — see the header. REFUSED rather than absent
          * while there is nothing to compare: the control is what tells a trainer that comparing
          * exists at all, and one that appeared only after a second answer arrived would teach
          * nothing. It keeps `aria-disabled` so the reason attached to it stays reachable. */}
        <RefusableIconButton
          className={String(styles.streamAction)}
          label={t(
            comparing === null
              ? 'web:clientDetail.surveys.compareOpen'
              : 'web:clientDetail.surveys.compareCancel',
          )}
          refusedReason={
            comparing !== null || comparable
              ? null
              : t('web:clientDetail.surveys.compareRefused')
          }
          onPress={comparing === null ? onEnterCompare : onExitCompare}
        >
          {comparing === null ? <CompareIcon /> : <CloseIcon />}
        </RefusableIconButton>
      </div>

      {stream.chips.length <= 2 ? null : (
        <div className={styles.streamChips}>
          {stream.chips.map((chip) => (
            /* REFUSED while a comparison is being picked, and focusable with its reason: narrowing
              * the list mid-pick takes the anchor's own row off the screen, and the trainer's next
              * press would be on a form the first pick cannot be compared with. The reason rides as
              * an sr-only tail rather than replacing the name — the accessible name has to contain
              * the visible label. */
            <button
              key={chip.id ?? 'all'}
              type="button"
              className={`${String(styles.formChip)} ${chip.selected ? String(styles.formChipOn) : ''}`}
              aria-pressed={chip.selected}
              aria-disabled={comparing !== null}
              onClick={() => {
                if (comparing === null) onChip(chip.id);
              }}
            >
              {chip.title}
              {comparing === null ? null : (
                <span className={hidden.srOnly}>
                  {' · '}
                  {t('web:clientDetail.surveys.chipRefusedComparing')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {bare ? null : (
      <div
        ref={scrollerRef}
        className={`${String(scrollbar.scrollbar)} ${String(styles.streamScroller)}`}
        role="region"
        tabIndex={scrolls ? 0 : undefined}
        aria-label={t('web:clientDetail.surveys.streamHeading')}
      >
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {/* The list's shape is rows, so its wait is bars where the rows will be — inside the
              * card's own reserve, so drawing them moves nothing. */}
            {gate.skeleton ? (
              <span className={styles.skelStream}>
                {SKELETON_ROWS.map((row) => (
                  <i key={row} className={`${String(skel.bar)} ${String(styles.skelStreamRow)}`} />
                ))}
              </span>
            ) : null}
          </>
        ) : failed || stream.empty ? (
          /* SILENT, and the box is kept. „Nie udało się wczytać ankiet." is said once, in the pane,
           * where it carries a retry; three copies of it across a strip, a list and a card is one
           * fact said three times. */
          null
        ) : stream.emptyForChip ? (
          /* A sentence rather than an `EmptyState`: this one is about the CHIP the trainer pressed
           * and not about the client, so it belongs beside the chips. */
          <p className={styles.streamNote}>{t('web:clientDetail.surveys.emptyForm')}</p>
        ) : (
          stream.months.map((month) => (
            <div className={styles.streamMonth} key={month.key}>
              <em className={styles.streamEyebrow}>{month.label}</em>
              {month.rows.map((row) => (
                <StreamRow
                  key={row.id}
                  row={row}
                  current={row.id === currentId}
                  comparing={comparing}
                  anchor={anchor}
                  onSelect={onSelect}
                  onToggleCompare={onToggleCompare}
                />
              ))}
            </div>
          ))
        )}
      </div>
      )}
      {truncated ? (
        <p className={styles.streamNote}>{t('web:clientDetail.surveys.streamTruncated')}</p>
      ) : null}
    </section>
  );
}

/**
 * One occurrence. A control in both modes and in neither when it was never answered.
 *
 * The visible title is the START of the accessible name and is never replaced by it — the date, the
 * outcome and the review state ride along as an sr-only tail, which is the rule the roster's row
 * link is written against.
 */
function StreamRow({
  row,
  current,
  comparing,
  anchor,
  onSelect,
  onToggleCompare,
}: {
  readonly row: SurveyStreamRow;
  readonly current: boolean;
  readonly comparing: readonly number[] | null;
  readonly anchor: string | null;
  readonly onSelect: (id: number) => void;
  readonly onToggleCompare: (id: number) => void;
}) {
  const { t } = useTranslation();

  const body = (
    <>
      <span className={styles.streamText}>
        <span className={styles.streamTitle}>{row.title}</span>
        <span className={styles.streamWhen}>{row.whenLabel}</span>
      </span>
      {row.chipLabel === null ? null : (
        <span
          className={`${String(styles.statusChip)} ${CHIP_TONE_CLASS[row.chipTone]}`}
        >
          {row.chipLabel}
        </span>
      )}
      {row.selectable && !row.reviewed ? (
        <>
          <i className={styles.dot} aria-hidden="true" />
          <span className={hidden.srOnly}>
            {' · '}
            {t('web:clientDetail.surveys.legendWaiting')}
          </span>
        </>
      ) : null}
    </>
  );

  if (!row.selectable) {
    return (
      <p className={`${String(styles.streamRow)} ${String(styles.streamRowMissed)}`}>
        {/* The checkbox's SPACE, so a row that cannot be checked does not jut out to the left of
          * every row above it while the mode is on. */}
        {comparing === null ? null : <i className={styles.streamBoxGap} aria-hidden="true" />}
        {body}
      </p>
    );
  }

  if (comparing !== null) {
    const refused = anchor !== null && anchor !== row.compareGroup;
    const checked = comparing.includes(row.id);
    return (
      <button
        type="button"
        role="checkbox"
        data-survey-row={row.id}
        aria-checked={checked}
        aria-disabled={refused}
        className={`${String(styles.streamRow)} ${checked ? String(styles.streamRowOn) : ''}`}
        onKeyDown={(event) => {
          // SPACE checks, ENTER opens. A `<button>` fires a click on both, which made Enter — the
          // key a trainer walking the list presses to read a week — silently tick a checkbox
          // instead. `role="checkbox"` promises Space; nothing promises Enter, so it keeps the
          // meaning it has in every other mode of this list.
          if (event.key !== 'Enter') return;
          event.preventDefault();
          onSelect(row.id);
        }}
        onClick={() => {
          if (!refused) onToggleCompare(row.id);
        }}
      >
        <i className={styles.streamBox} aria-hidden="true" />
        {body}
        {refused ? (
          <span className={hidden.srOnly}> · {t('web:clientDetail.surveys.compareOtherForm')}</span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-survey-row={row.id}
      aria-current={current ? 'true' : undefined}
      className={`${String(styles.streamRow)} ${current ? String(styles.streamRowOn) : ''}`}
      onClick={() => {
        onSelect(row.id);
      }}
    >
      {body}
      <span className={hidden.srOnly}> · {t('web:clientDetail.surveys.openReport')}</span>
    </button>
  );
}
